import { defineStore } from "pinia";
import { ref, inject } from "vue";
import TonWeb from "tonweb";
import { isIOSDevice, _twa_encode, setLocalStorage } from "../utils/common";
import {
  generateOrder,
  createOrder,
  checkOrderStatus,
  createOrderV2,
  checkOrderStatusV2,
  checkOrderV4,
} from "../api/TON";
import { updateProfile } from "../api/user";
import emitter from "../utils/bus";
import { useUserStore } from "./user";

let userStore = () => useUserStore();

export const useTonConnectStore = defineStore("Ton", () => {
  // 状态变量
  const TonConnect = ref(false);
  const connectLoading = ref(false);
  const addressUtils = TonWeb.utils.Address;
  const walletAddress = ref(null); // 处理过后的地址
  const $swal = inject("$swal");
  const isLinked = ref(false);
  const isConnected = ref(false);
  function setTonConnect(connect) {
    TonConnect.value = connect;
    isLinked.value = connect.connected || userStore().ton_address == "";
  }

  // 链接钱包
  async function linkedWallet(isUpdate = false) {
    if (connectLoading.value) return;
    connectLoading.value = true;

    try {
      if (!TonConnect.value) {
        throw new Error("TonConnectUI not initialized");
      }

      if (!TonConnect.value.connected) {
        await TonConnect.value.connectWallet();
      }

      const wallet = TonConnect.value.wallet;
      if (wallet?.account?.address) {
        const addressRaw = new addressUtils(wallet.account.address);
        walletAddress.value = addressRaw.toString(true);
        userStore().setTonAddress(wallet.account.address);
        isConnected.value = true;
        setLocalStorage("LAST_CONNECT_TIME", new Date().getTime());
        isLinked.value = true;
        if (isUpdate) {
          return await saveWall(wallet.account.address);
        } else {
          return addressRaw;
        }
      } else {
        throw new Error("Wallet connection failed or address not available");
      }
    } catch (error) {
      console.error("Failed to connect wallet:", error);
    } finally {
      connectLoading.value = false;
    }
    return null;
  }

  let saveWall = async (address) => {
    //保存云端（使用更新用户信息接口）
    let res = await updateProfile({
      payment_type: "ton",
      payment_info: address,
    });

    if (res.code == 200) {
      userStore().setPaymentInfo(address, "ton");
      // toastSuccess($swal, `保存成功`);
      return address;
    } else {
      return null;
    }
  };

  /**
   * 检查状态
   * @param {function} back - 请求函数
   * @param {object} back_data - 请求参数
   * @param {number} maxAwaitRetries - 支付等待状态最大重试次数
   * @param {number} maxErrorRetries - 请求失败最大连续重试次数
   * @param {number} interval - 重试间隔时间(毫秒)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  const checkPaymentStatus = async ({
    back,
    back_data,
    maxAwaitRetries = 10,
    maxErrorRetries = 3,
    interval = 10000,
  }) => {
    let awaitRetryCount = 0; // 支付等待状态的重试次数
    let errorRetryCount = 0; // 连续失败/异常的重试次数

    const check = async () => {
      try {
        const checkRes = await back(back_data);

        if (checkRes.code !== 200) {
          throw new Error("检查订单状态失败");
        }

        // 请求成功后重置错误重试计数
        errorRetryCount = 0;

        switch (checkRes.data) {
          case "success":
            return { success: true, message: "支付成功" };
          case "wait":
            if (awaitRetryCount >= maxAwaitRetries) {
              return { success: false, message: "支付超时，请重试" };
            }
            awaitRetryCount++;
            await new Promise((resolve) => setTimeout(resolve, interval));
            return check();
          default:
            return { success: false, message: "支付失败，请后再试" };
        }
      } catch (error) {
        errorRetryCount++;

        if (errorRetryCount >= maxErrorRetries) {
          return {
            success: false,
            message: "系统异常，请稍后再试",
          };
        }

        await new Promise((resolve) => setTimeout(resolve, interval));
        return check();
      }
    };

    return check();
  };

  // 转账
  /**
   *
   * @param {object} item  任务item
   * @returns
   */
  const handlePayOnClick3 = async (item) => {
    const order_amount = item.app_chatid;
    const id = item.id;
    if (!TonConnect.value.connected || userStore().ton_address == "") {
      linkedWallet();
      emitter.emit("orderTaskResult", false); // 触发任务未完成
      return;
    }

    let order_id = "";
    try {
      // 创建订单
      let generateRes = await createOrderV2({ id });
      if (generateRes.code != 200) {
        emitter.emit("orderTaskResult", false); // 触发任务未完成
        return;
      }
      let generateResData = generateRes.data; // 保存单号

      order_id = generateResData.order_id;
      // 支付的数量
      const payNano = TonWeb.utils.toNano(order_amount).toString();

      // 转账备注
      let cell = new TonWeb.boc.Cell();
      cell.bits.writeUint(0, 32);
      cell.bits.writeString(generateResData.order_id);
      let payload = TonWeb.utils.bytesToBase64(await cell.toBoc());

      // 交易参数拼接
      const transaction = {
        validUntil: Math.round(Date.now() / 1000) + 6000,
        messages: [
          {
            address: generateResData.url,
            amount: payNano,
            payload: payload,
          },
        ],
      };

      // 浏览器环境：iOS 设备自动打开钱包链接
      let isIOS = isIOSDevice();
      if (isIOS) {
        setTimeout(() => {
          window.open(
            "https://t.me/wallet/start?startapp=tonconnect-ret__back",
            "_blank",
            "noopener,noreferrer"
          );
        }, 2000);
      }
      // 发送交易
      await TonConnect.value.sendTransaction(transaction);

      /* 展示检查弹窗，把任务详情、订单类型/ID传进去 */
      emitter.emit("showLoadingModal", {
        isShow: true,
        taskDetail: item,
        orderDetail: {
          type: "ton",
          order_id: generateResData.order_id,
        },
      });
    } catch (e) {
      console.error("Transaction failed:", e);
      emitter.emit("orderTaskResult", false); // 触发任务未完成
      // 存在订单号，检查一下订单状态
      if (order_id != "") {
        checkOrderStatusV2(order_id);
      }
    }
  };

  // 开始交易
  /**
   *
   * @param {number} order_amount 支付金额
   * @param {object} generateResData 订单数据
   * @returns
   */
  const startTonTransaction = async (order_amount, generateResData) => {
    if (!TonConnect.value.connected || userStore().ton_address == "") {
      linkedWallet();
      return "linkedWallet";
    }

    let order_id = "";
    try {
      // 支付的数量
      const payNano = TonWeb.utils.toNano(order_amount).toString();

      // 转账备注
      let cell = new TonWeb.boc.Cell();
      cell.bits.writeUint(0, 32);
      cell.bits.writeString(generateResData.order_id);
      let payload = TonWeb.utils.bytesToBase64(await cell.toBoc());
      order_id = generateResData.order_id;

      // 交易参数拼接
      const transaction = {
        validUntil: Math.round(Date.now() / 1000) + 6000,
        messages: [
          {
            address: generateResData.ton_address,
            amount: payNano,
            payload: payload,
          },
        ],
      };

      // 浏览器环境：iOS 设备自动打开钱包链接
      let isIOS = isIOSDevice();
      if (isIOS) {
        setTimeout(() => {
          window.open(
            "https://t.me/wallet/start?startapp=tonconnect-ret__back",
            "_blank",
            "noopener,noreferrer"
          );
        }, 2000);
      }

      // 发送交易
      await TonConnect.value.sendTransaction(transaction);

      /* 展示检查弹窗，把任务详情、订单类型/ID传进去 */
      const check_result = await checkPaymentStatus({
        back: checkOrderV4,
        back_data: { order_id },
        maxAwaitRetries: 10,
        maxErrorRetries: 3,
        interval: 10000,
      });
      if (check_result.success) {
        console.log(check_result.message);
        return true;
      } else {
        console.log(check_result.message);
        return false;
      }
    } catch (e) {
      console.error("Transaction failed:", e);
      // 存在订单号，检查一下订单状态
      if (order_id != "") {
        checkOrderV4({ order_id });
      }
      return false;
    }
  };

  /**
   * 发送TON链上的USDT交易
   * @param {number} order_amount 支付金额（USDT金额，以美元为单位）
   * @param {object} generateResData 订单数据
   * @returns
   */
  const startUsdtTransaction = async (order_amount, generateResData) => {
    if (!TonConnect.value.connected || userStore().ton_address == "") {
      linkedWallet();
      return "linkedWallet";
    }

    let order_id = "";
    try {
      // 获取订单ID
      order_id = generateResData.order_id;

      // USDT主合约地址 - 使用正确的TON链上USDT合约地址
      const USDT_MASTER_ADDRESS =
        "EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs";

      // 商户接收USDT的钱包地址
      const INVOICE_WALLET_ADDRESS = generateResData.ton_address;

      // 用户的TON地址
      const userAddress = TonConnect.value.wallet.account.address;

      // USDT有6位小数，计算实际支付的最小单位金额
      const usdtAmount = BigInt(Math.round(order_amount * 1000000));

      // 创建TonWeb实例
      const tonweb = new TonWeb(
        new TonWeb.HttpProvider("https://toncenter.com/api/v2/jsonRPC")
      );

      // 通过主合约地址获取用户的USDT钱包地址
      // 注意：这是一个示例，实际实现可能需要根据您的环境调整
      const jettonMasterContract = new tonweb.token.jetton.JettonMinter(
        USDT_MASTER_ADDRESS
      );
      const userUsdtWalletAddress =
        await jettonMasterContract.getJettonWalletAddress(userAddress);

      // 创建转账载荷
      let transferCell = new TonWeb.boc.Cell();
      transferCell.bits.writeUint(0xf8a7ea5, 32); // transfer操作码
      transferCell.bits.writeUint(0, 64); // queryId
      transferCell.bits.writeCoins(usdtAmount); // jetton amount

      // 写入接收方地址
      const invoiceAddressCell = new TonWeb.boc.Cell();
      const invoiceAddressObj = new TonWeb.utils.Address(
        INVOICE_WALLET_ADDRESS
      );
      invoiceAddressCell.bits.writeAddress(invoiceAddressObj);
      transferCell.refs.push(invoiceAddressCell);

      // 写入响应地址（用户地址）
      const userAddressCell = new TonWeb.boc.Cell();
      const userAddressObj = new TonWeb.utils.Address(userAddress);
      userAddressCell.bits.writeAddress(userAddressObj);
      transferCell.refs.push(userAddressCell);

      // 无自定义数据
      transferCell.bits.writeBit(0);

      // 转发金额
      transferCell.bits.writeCoins(1); // 1 nanoTON

      // 写入订单ID作为注释
      const commentCell = new TonWeb.boc.Cell();
      commentCell.bits.writeUint(0, 32);
      commentCell.bits.writeString(order_id);

      // 有转发数据
      transferCell.bits.writeBit(1);
      transferCell.refs.push(commentCell);

      // 序列化为base64
      const transferPayload = TonWeb.utils.bytesToBase64(
        await transferCell.toBoc()
      );

      // 交易参数
      const transaction = {
        validUntil: Math.round(Date.now() / 1000) + 6000,
        messages: [
          {
            address: userUsdtWalletAddress.toString(),
            amount: "100000000", // 0.1 TON作为gas费用
            payload: transferPayload,
          },
        ],
      };

      // 浏览器环境：iOS 设备自动打开钱包链接
      let isIOS = isIOSDevice();
      if (isIOS) {
        setTimeout(() => {
          window.open(
            "https://t.me/wallet/start?startapp=tonconnect-ret__back",
            "_blank",
            "noopener,noreferrer"
          );
        }, 2000);
      }

      // 发送交易
      await TonConnect.value.sendTransaction(transaction);

      /* 展示检查弹窗，把任务详情、订单类型/ID传进去 */
      const check_result = await checkPaymentStatus({
        back: checkOrderV4,
        back_data: { order_id },
        maxAwaitRetries: 10,
        maxErrorRetries: 3,
        interval: 10000,
      });

      if (check_result.success) {
        console.log(check_result.message);
        return true;
      } else {
        console.log(check_result.message);
        return false;
      }
    } catch (e) {
      console.error("USDT Transaction failed:", e);
      // 存在订单号，检查一下订单状态
      if (order_id != "") {
        checkOrderV4({ order_id });
      }
      return false;
    }
  };

  // 返回store的公共接口
  return {
    setTonConnect,
    TonConnect,
    linkedWallet,
    walletAddress,
    checkPaymentStatus,
    handlePayOnClick3,
    startTonTransaction,
    isLinked,
    isConnected,
    startUsdtTransaction,
  };
});
